import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './KeywordsPage.scss';
import Title from '../../shared/components/title/Title';
import { useTranslation } from 'react-i18next';
import Loader from '../../shared/components/common/loader/Loader';
import { useNavigate, useParams } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import { PROJECTS } from '../../shared/routes/path';
import { useGetProjectKeywords } from '../../shared/api/hooks/monitor/keyword/useGetProjectKeywords';
import { useUpdateKeywordStatus } from '../../shared/api/hooks/monitor/keyword/useUpdateKeywordStatus';
import { useGetTags } from '../../shared/api/hooks/tags/useGetTags';
import { KeywordResponse, KeywordStatus } from '../../shared/models/monitor/KeywordResponse';
import { bulkUpdateKeywordStatus } from '../../shared/services/monitor/keywords/KeywordsService';
import { GET_PROJECT_KEYWORDS } from '../../shared/api/query/keyword/keys';
import { GET_PROJECT_ALL_BRANDS_RESPONSE, GET_PROJECT_DAILY_STATS } from '../../shared/api/query/project/stats/keys';
import { GET_KEYWORDS_DAILY_STATS_FOR_PROJECT } from '../../shared/api/query/keyword/stats/keys';
import CustomTooltip from '../../shared/components/common/tooltip/CustomTooltip';
import EmptyState from '../../shared/components/common/empty-state/EmptyState';
import { keywordsTemplate } from '../../shared/helpers/ColumnHelper';

type FilterMode = 'ALL' | 'ACTIVE' | 'INACTIVE';

const KeywordsPage: React.FC = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const projectIdNumber = Number(projectId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { accountResponse, projectResponse } = useAccount();

  useEffect(() => {
    if (accountResponse && !accountResponse.addons.includes('KEYWORDS_PAGE')) {
      navigate(PROJECTS, { replace: true });
    }
  }, [accountResponse, navigate]);

  const { keywords, isLoading } = useGetProjectKeywords(projectIdNumber);
  const { tags, isLoading: isTagsLoading } = useGetTags(projectIdNumber);

  const [filterMode, setFilterMode] = useState<FilterMode>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedKeywords, setSelectedKeywords] = useState<KeywordResponse[]>([]);

  const handleMutationError = useCallback(() => {
    setShowErrorToast(true);
  }, []);

  const updateStatus = useUpdateKeywordStatus(projectIdNumber, { onError: handleMutationError });

  const invalidateKeywords = useCallback(
    () => {
      queryClient.invalidateQueries({ queryKey: [GET_PROJECT_KEYWORDS, projectIdNumber] });
      queryClient.invalidateQueries({ queryKey: [GET_PROJECT_ALL_BRANDS_RESPONSE, projectIdNumber] });
      queryClient.invalidateQueries({ queryKey: [GET_PROJECT_DAILY_STATS] });
      queryClient.invalidateQueries({ queryKey: [GET_KEYWORDS_DAILY_STATS_FOR_PROJECT] });
    },
    [queryClient, projectIdNumber]
  );

  const bulkActivateMutation = useMutation({
    mutationFn: () =>
      bulkUpdateKeywordStatus(
        selectedKeywords.map((kw) => kw.id),
        'ACTIVE'
      ),
    onSuccess: () => {
      setSelectedKeywords([]);
      invalidateKeywords();
    },
    onError: handleMutationError,
  });

  const bulkDeactivateMutation = useMutation({
    mutationFn: () =>
      bulkUpdateKeywordStatus(
        selectedKeywords.map((kw) => kw.id),
        'INACTIVE'
      ),
    onSuccess: () => {
      setSelectedKeywords([]);
      invalidateKeywords();
    },
    onError: handleMutationError,
  });

  useEffect(() => {
    if (!showErrorToast) return;
    const timer = setTimeout(() => setShowErrorToast(false), 3000);
    return () => clearTimeout(timer);
  }, [showErrorToast]);

  const currentProject = useMemo(() => {
    if (!projectResponse || !projectIdNumber) return null;
    return projectResponse.find((p) => p.id === projectIdNumber) ?? null;
  }, [projectResponse, projectIdNumber]);

  const defaultKeyword = currentProject?.defaultKeyword ?? '';
  const firmName = currentProject?.firmName ?? '';

  const tagLabelById = useMemo(() => {
    const map = new Map<number, string>();
    tags.forEach((tag) => {
      map.set(tag.id, tag.label);
    });
    return map;
  }, [tags]);

  const filteredKeywords = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return keywords
      .filter((kw) => kw.normalizedKeyword !== defaultKeyword)
      .filter((kw) => {
        if (filterMode === 'ACTIVE') return kw.status === 'ACTIVE';
        if (filterMode === 'INACTIVE') return kw.status === 'INACTIVE';
        return true;
      })
      .filter((kw) => (query ? kw.keyword.toLowerCase().includes(query) : true))
      .sort((a, b) => a.keyword.localeCompare(b.keyword));
  }, [keywords, filterMode, searchQuery, defaultKeyword]);

  const isRowSelectable = useCallback(
    (kw: KeywordResponse): boolean => {
      if (kw.normalizedKeyword === defaultKeyword) return false;
      if (kw.normalizedKeyword === firmName) return false;
      if (kw.tagId != null) return false;
      return true;
    },
    [defaultKeyword, firmName]
  );

  const isToggleDisabled = (kw: KeywordResponse): boolean => {
    if (kw.normalizedKeyword === defaultKeyword) return true;
    if (kw.normalizedKeyword === firmName) return true;
    return kw.tagId != null;
  };

  const getDisabledReason = (kw: KeywordResponse): string => {
    if (kw.normalizedKeyword === defaultKeyword) return t('keywordToggleDisabledDefault');
    if (kw.normalizedKeyword === firmName) return t('keywordToggleDisabledFirm');
    if (kw.tagId !== null && kw.tagId !== undefined) return t('keywordToggleDisabledTag');
    return '';
  };

  const handleToggle = (kw: KeywordResponse) => {
    if (isToggleDisabled(kw)) return;
    const newStatus: KeywordStatus = kw.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    updateStatus.mutate({ keywordId: kw.id, status: newStatus });
  };

  const hasAnyInactive = selectedKeywords.some((kw) => {
    const current = filteredKeywords.find((f) => f.id === kw.id);
    return (current ?? kw).status === 'INACTIVE';
  });

  const hasAnyActive = selectedKeywords.some((kw) => {
    const current = filteredKeywords.find((f) => f.id === kw.id);
    return (current ?? kw).status === 'ACTIVE';
  });

  if (!accountResponse || !projectResponse || !projectId) {
    navigate(PROJECTS);
    return <></>;
  }

  if (isLoading || isTagsLoading) {
    return <Loader />;
  }

  if (filteredKeywords.length === 0 && !searchQuery && filterMode === 'ALL') {
    return (
      <div className="keywords-page page-content">
        <div className="page-box">
          <Title
            title={t('keywords')}
            childrenOnMain={<div className="title-box__subtitle">{t('keywordsPageDesc')}</div>}
          />
          <div className="empty-state-page">
            <EmptyState emptyStateData={{ title: t('keywordsEmptyState') }} />
          </div>
        </div>
      </div>
    );
  }

  const toggleBodyTemplate = (kw: KeywordResponse) => {
    const disabled = isToggleDisabled(kw) || updateStatus.isPending;
    const title = isToggleDisabled(kw) ? getDisabledReason(kw) : undefined;
    return (
      <div title={title} onClick={() => !disabled && handleToggle(kw)} style={{ cursor: disabled ? 'default' : 'pointer' }}>
        <InputSwitch
          checked={kw.status === 'ACTIVE'}
          disabled={disabled}
        />
      </div>
    );
  };

  const tagBodyTemplate = (kw: KeywordResponse) => {
    if (kw.tagId == null) return null;
    const label = tagLabelById.get(kw.tagId);
    if (!label) return null;
    return keywordsTemplate([label], 1);
  };

  const promptCountBodyTemplate = (kw: KeywordResponse) => {
    return <span className="keyword-prompt-count">{kw.promptCount ?? 0}</span>;
  };

  const filterOptions = [
    { label: t('keywordFilterAll'), value: 'ALL' },
    { label: t('keywordFilterActive'), value: 'ACTIVE' },
    { label: t('keywordFilterInactive'), value: 'INACTIVE' },
  ];

  return (
    <div className="keywords-page page-content">
      <div className="page-box">
        <Title
          title={t('keywords')}
          childrenOnMain={<div className="title-box__subtitle">{t('keywordsPageDesc')}</div>}
        />

        <div className="page-box__stats">
          <div className="table-content">
            <div className="keywords-page__filter-bar">
              <Dropdown
                value={filterMode}
                options={filterOptions}
                onChange={(e) => setFilterMode(e.value)}
                className="dropdown dropdown--btn-sm keywords-page__status-dropdown"
              />
              <div className="page-box__buttons">
                {selectionMode && selectedKeywords.length > 0 && (
                  <div className="page-box__buttons-panel">
                    {hasAnyInactive && (
                      <>
                        <Button
                          className="action-btn"
                          onClick={() => bulkActivateMutation.mutate()}
                          disabled={bulkActivateMutation.isPending}
                          id="bulk-activate-keywords-btn"
                        >
                          <span className="ico--run ico--xs" />
                        </Button>
                        <CustomTooltip
                          tooltipText="keywordBulkActivate"
                          targetRef="bulk-activate-keywords-btn"
                        />
                      </>
                    )}
                    {hasAnyActive && (
                      <>
                        <Button
                          className="action-btn"
                          onClick={() => bulkDeactivateMutation.mutate()}
                          disabled={bulkDeactivateMutation.isPending}
                          id="bulk-deactivate-keywords-btn"
                        >
                          <span className="ico--pause ico--xs" />
                        </Button>
                        <CustomTooltip
                          tooltipText="keywordBulkDeactivate"
                          targetRef="bulk-deactivate-keywords-btn"
                        />
                      </>
                    )}
                  </div>
                )}
                <div className="input-search-box">
                  <InputText
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('searchKeywords')}
                    className="input-search"
                  />
                </div>
                <Button
                  className={`action-btn${selectionMode ? ' action-btn--active' : ''}`}
                  onClick={() => {
                    setSelectionMode((prev) => {
                      if (prev) setSelectedKeywords([]);
                      return !prev;
                    });
                  }}
                  id="keyword-select-mode-btn"
                >
                  <span className="ico--select ico--xs" />
                </Button>
                <CustomTooltip tooltipText="keywordSelectMode" targetRef="keyword-select-mode-btn" />
              </div>
            </div>

            <DataTable
              stripedRows
              removableSort
              paginator
              paginatorClassName="pagination"
              rows={25}
              rowsPerPageOptions={[10, 25, 50]}
              currentPageReportTemplate={t('rowsNumber') + ':'}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              value={filteredKeywords}
              tableStyle={{ minWidth: '30rem', tableLayout: 'auto' }}
              tableClassName={`table${selectionMode ? ' table--select' : ''}`}
              className="table-wrapper"
              emptyMessage={t('noKeywordsFound')}
            >
              <Column
                key="checkbox"
                exportable={false}
                hidden={!selectionMode}
                headerStyle={{ width: '25px' }}
                header={
                  <Checkbox
                    checked={
                      filteredKeywords.filter(isRowSelectable).length > 0 &&
                      filteredKeywords
                        .filter(isRowSelectable)
                        .every((kw) => selectedKeywords.some((s) => s.id === kw.id))
                    }
                    onChange={(e) => {
                      setSelectedKeywords(e.checked ? filteredKeywords.filter(isRowSelectable) : []);
                    }}
                  />
                }
                body={(kw: KeywordResponse) => (
                  <Checkbox
                    checked={selectedKeywords.some((s) => s.id === kw.id)}
                    onChange={(e) => {
                      if (!isRowSelectable(kw)) return;
                      if (e.checked) {
                        setSelectedKeywords((prev) => [...prev, kw]);
                      } else {
                        setSelectedKeywords((prev) => prev.filter((s) => s.id !== kw.id));
                      }
                    }}
                    disabled={!isRowSelectable(kw)}
                  />
                )}
              />
              <Column
                sortable
                field="keyword"
                header={t('keyword')}
                headerClassName="table__header"
                style={{ minWidth: '180px', maxWidth: '100%' }}
              />
              <Column
                header={t('tags')}
                body={tagBodyTemplate}
                headerClassName="table__header"
                style={{ width: '160px' }}
              />
              <Column
                sortable
                field="promptCount"
                header={t('keywordPromptCount')}
                body={promptCountBodyTemplate}
                headerClassName="table__header table__header--center"
                bodyClassName="table__cell--center"
                style={{ width: '140px' }}
                sortFunction={(e) => {
                  const data = [...(e.data as KeywordResponse[])];
                  const order = e.order ?? 1;
                  data.sort((a, b) => order * ((a.promptCount ?? 0) - (b.promptCount ?? 0)));
                  return data;
                }}
              />
              <Column
                header={t('status')}
                body={toggleBodyTemplate}
                headerClassName="table__header table__header--center"
                bodyClassName="table__cell table__cell--center"
                style={{ width: '80px' }}
              />
            </DataTable>
          </div>
        </div>
      </div>

      {showErrorToast && <div className="toast">{t('keywordUpdateError')}</div>}
    </div>
  );
};

export default KeywordsPage;
